import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useId } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import styles from './RichTextEditor.module.scss';
// Keep this allowlist in sync with server/app/utils/html_sanitizer.py's
// ALLOWED_TAGS - anything the toolbar can produce that the server would
// strip on save would silently vanish on reload.
export const RichTextEditor = ({ value, onChange, label, id: propId }) => {
    const generatedId = useId();
    const editorId = propId || generatedId;
    const labelId = `${editorId}-label`;
    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: false,
                blockquote: false,
                codeBlock: false,
                code: false,
                horizontalRule: false,
                strike: false,
                link: { openOnClick: false },
            }),
        ],
        content: value,
        onUpdate: ({ editor: updatedEditor }) => {
            onChange(updatedEditor.getHTML());
        },
        // TipTap renders `id`/other DOM attrs passed to <EditorContent> on its
        // outer wrapper, not on the actual contenteditable node - so a <label
        // htmlFor> pointing at that id never focuses or associates with
        // anything. aria-labelledby on the contenteditable itself is the fix.
        editorProps: {
            attributes: {
                'aria-labelledby': labelId,
            },
        },
    });
    useEffect(() => {
        if (!editor)
            return;
        if (editor.getHTML() !== value) {
            editor.commands.setContent(value, { emitUpdate: false });
        }
    }, [value, editor]);
    if (!editor)
        return null;
    const setLink = () => {
        const previousUrl = editor.getAttributes('link').href;
        // eslint-disable-next-line no-alert
        const url = window.prompt('URL', previousUrl || '');
        if (url === null)
            return;
        if (url === '') {
            editor.chain().focus().extendMarkRange('link').unsetLink().run();
            return;
        }
        editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    };
    const toolbarButtons = [
        {
            label: 'B',
            ariaLabel: 'Bold',
            isActive: editor.isActive('bold'),
            onClick: () => editor.chain().focus().toggleBold().run(),
        },
        {
            label: 'I',
            ariaLabel: 'Italic',
            isActive: editor.isActive('italic'),
            onClick: () => editor.chain().focus().toggleItalic().run(),
        },
        {
            label: 'U',
            ariaLabel: 'Underline',
            isActive: editor.isActive('underline'),
            onClick: () => editor.chain().focus().toggleUnderline().run(),
        },
        {
            label: '• List',
            ariaLabel: 'Bullet list',
            isActive: editor.isActive('bulletList'),
            onClick: () => editor.chain().focus().toggleBulletList().run(),
        },
        {
            label: '1. List',
            ariaLabel: 'Numbered list',
            isActive: editor.isActive('orderedList'),
            onClick: () => editor.chain().focus().toggleOrderedList().run(),
        },
        {
            label: 'Link',
            ariaLabel: 'Link',
            isActive: editor.isActive('link'),
            onClick: setLink,
        },
    ];
    return (_jsxs("div", { className: styles.wrapper, children: [label && (_jsx("span", { id: labelId, className: styles.label, children: label })), _jsxs("div", { className: styles.toolbar, role: "toolbar", "aria-label": label ? `${label} formatting` : 'Formatting', children: [toolbarButtons.map((button) => (_jsx("button", { type: "button", "aria-label": button.ariaLabel, "aria-pressed": button.isActive, className: [styles.toolbarButton, button.isActive ? styles.toolbarButtonActive : '']
                            .filter(Boolean)
                            .join(' '), onClick: button.onClick, children: button.label }, button.ariaLabel))), _jsx("button", { type: "button", "aria-label": "Clear formatting", className: styles.toolbarButton, onClick: () => editor.chain().focus().unsetAllMarks().clearNodes().run(), children: "Clear" })] }), _jsx(EditorContent, { editor: editor, className: styles.content, 
                // The contenteditable node is only as tall as its own content, not
                // this wrapper's min-height - like a native <textarea>, clicking
                // anywhere in the visible box (including the padding) should focus
                // it, not just clicking exactly on existing text.
                onClick: () => editor.commands.focus() })] }));
};
